<!-- ======================================================================== -->
<!-- LOADING STATE                                                            -->
<!-- ======================================================================== -->
@if (isLoading) {
  <div class="notes-detail__loading">
    <ng-icon name="evaSpinner" class="eva-icon-spin"></ng-icon>
  </div>
}

<!-- ======================================================================== -->
<!-- DETAIL VIEW                                                              -->
<!-- ======================================================================== -->
@if (!isLoading && note) {
  <div class="notes-detail">
    <!-- ================================================================== -->
    <!-- HEADER : retour + pin + menu                                        -->
    <!-- ================================================================== -->
    <div class="notes-detail__header">
      <eva-button icon="heroArrowLeft" variant="text" size="small" (onClick)="goBack()" ariaLabel="Retour"></eva-button>

      <div class="notes-detail__header-actions">
        <div class="notes-detail__pin-slot">
          @if (isPinned) {
            <eva-tag [preset]="pinnedTagPreset" size="small" [rounded]="true" (click)="togglePin()"></eva-tag>
          } @else {
            <eva-button
              icon="evaPin"
              variant="text"
              size="small"
              severity="secondary"
              (onClick)="togglePin()"
              ariaLabel="Épingler"
            ></eva-button>
          }
        </div>

        <div class="notes-detail__pin-slot">
          @if (isPrivate) {
            <eva-tag [preset]="privateTagPreset" size="small" [rounded]="true" (click)="togglePrivate()"></eva-tag>
          } @else {
            <eva-button
              icon="heroLockOpen"
              variant="text"
              size="small"
              severity="secondary"
              (onClick)="togglePrivate()"
              ariaLabel="Rendre privée"
            ></eva-button>
          }
        </div>

        <div class="notes-detail__pin-slot">
          @if (isUrgent) {
            <eva-tag [preset]="urgentTagPreset" size="small" [rounded]="true" (click)="toggleUrgent()"></eva-tag>
          } @else {
            <eva-button
              icon="heroFlag"
              variant="text"
              size="small"
              severity="secondary"
              (onClick)="toggleUrgent()"
              ariaLabel="Marquer comme urgent"
            ></eva-button>
          }
        </div>

        <eva-menu [items]="contextMenuItems" position="bottom-right">
          <ng-template #menuTrigger>
            <eva-button icon="heroEllipsisVertical" variant="text" size="small" ariaLabel="Menu"></eva-button>
          </ng-template>
        </eva-menu>
      </div>
    </div>

    <!-- ================================================================== -->
    <!-- ÉDITEUR UNIFIÉ (première ligne = titre)                              -->
    <!-- ================================================================== -->
    <div class="notes-detail__editor notes-detail__editor--unified">
      <eva-markdown-editor
        [value]="editorValue"
        (valueChange)="onEditorChange($event)"
        placeholder="Titre de la note"
        [maxlength]="MAX_CONTENT_LENGTH"
        [showCount]="true"
      ></eva-markdown-editor>
    </div>

    <!-- ================================================================== -->
    <!-- RATTACHEMENTS / PARTAGE                                            -->
    <!-- ================================================================== -->
    <div class="notes-detail__attachments">
      <!-- Boutons actions + panneaux inline -->
      <div class="notes-detail__attachment-actions">
        <!-- Lier à... + tags existants sur la même ligne -->
        <div class="notes-detail__attachment-row">
          <div class="notes-detail__lier-wrapper">
            <eva-button
              label="Lier à..."
              icon="heroLink"
              variant="text"
              size="small"
              (onClick)="toggleAttachmentSearch()"
            ></eva-button>

            @if (showAttachmentSearch) {
              <div class="notes-detail__attachment-overlay" (click)="toggleAttachmentSearch()"></div>
              <div class="notes-detail__attachment-dropdown">
                <eva-input
                  icon="heroMagnifyingGlass"
                  [clearable]="true"
                  placeholder="Rechercher un chantier, client, offre..."
                  size="inline"
                  [value]="attachmentSearchQuery"
                  (valueChange)="onAttachmentSearch($event)"
                  [autofocus]="true"
                ></eva-input>

                @if (isSearchingAttachments) {
                  <div class="notes-detail__attachment-searching">
                    <ng-icon name="evaSpinner" class="eva-icon-spin"></ng-icon>
                    <span>Recherche en cours...</span>
                  </div>
                }

                @if (attachmentSearchResults.length > 0) {
                  <div class="notes-detail__attachment-results">
                    @for (group of attachmentSearchResults; track group.object_type) {
                      <div class="notes-detail__attachment-group">
                        <div class="notes-detail__attachment-group-label">
                          <i class="pi" [ngClass]="group.object_type_icon"></i>
                          {{ group.object_type_label }}
                        </div>
                        @for (item of group.items; track item.object_id) {
                          <button
                            type="button"
                            class="notes-detail__attachment-result-item"
                            (click)="addAttachment(group.object_type, item.object_id, item.label)"
                          >
                            {{ item.label }}
                          </button>
                        }
                      </div>
                    }
                  </div>
                }
              </div>
            }
          </div>

          @for (att of note!.attachments; track att.object_id) {
            <eva-tag
              [preset]="getAttachmentPreset(att)"
              [removable]="true"
              (click)="openAttachment(att)"
              (onRemove)="removeAttachment(att)"
            ></eva-tag>
          }
        </div>

        <!-- Partager à... -->
        <app-task-assignee-picker
          [(assignees)]="sharedWith"
          [employees]="employees"
          placeholder="Partager à..."
        ></app-task-assignee-picker>
      </div>
    </div>

    <!-- ================================================================== -->
    <!-- FOOTER — Auteur, dates, statut sauvegarde                          -->
    <!-- ================================================================== -->
    <div class="notes-detail__footer">
      <div class="notes-detail__footer-dates">
        <span class="notes-detail__footer-meta">{{ createdInfo }}</span>
        <span class="notes-detail__footer-meta">{{ updatedInfo }}</span>
      </div>

      @if (saveStatus !== "idle") {
        <div
          class="notes-detail__footer-status"
          [class.notes-detail__footer-status--saving]="saveStatus === 'saving'"
          [class.notes-detail__footer-status--saved]="saveStatus === 'saved'"
          [class.notes-detail__footer-status--error]="saveStatus === 'error'"
        >
          @if (saveStatus === "saving") {
            <ng-icon name="evaSpinner" class="eva-icon-spin"></ng-icon>
          } @else if (saveStatus === "saved") {
            <ng-icon name="heroCheck"></ng-icon>
          } @else if (saveStatus === "error") {
            <ng-icon name="heroXMark"></ng-icon>
          }
          <span>{{ saveStatusLabel }}</span>
        </div>
      }
    </div>
  </div>
}

<!-- ======================================================================== -->
<!-- CONFIRM DIALOG                                                           -->
<!-- ======================================================================== -->
<eva-dialog
  [(visible)]="showDeleteDialog"
  header="Supprimer cette note ?"
  type="confirm"
  confirmLabel="Supprimer"
  cancelLabel="Annuler"
  confirmSeverity="danger"
  size="sm"
  (onConfirm)="executeDelete()"
  (onCancel)="showDeleteDialog = false"
>
  <p>La note sera archivée et pourra être restaurée par un administrateur.</p>
</eva-dialog>
